import { defineComponent as _defineComponent } from 'vue'
import { useProformaStore } from '@/modules/proforma/stores/proformaStore';
import { useApplicationStore } from '@/store/applicationStore';
import { computed, ref, watch, onActivated } from 'vue';
import { ColumnDef } from '@/types/ColumnDef';
import EmailGenerator from '@/app-components/EmailGenerator.vue';
import router from '@/router/router';
import { EnquiryListItem, ProformaEmailTemplateItem } from '@/types/ProformaTypes';
import { GlobalHelperFunctions } from '@/common/GlobalHelperFunctions';
import { generateDocument } from '@/services/api/api-calls/generateDocument';


export default /*#__PURE__*/_defineComponent({
  __name: 'EnquiryList',
  setup(__props) {

// eslint-disable-next-line import/no-cycle
const proformaStore = useProformaStore();
const applicationStore = useApplicationStore();
const isLoading = ref(false);
const agencyProformas = computed<EnquiryListItem[]>(() => proformaStore.agencyProformas);
const emailTemplates = computed<ProformaEmailTemplateItem[]>(() => proformaStore.emailTemplates);
const selectedProformaIds = ref<number[]>([]);
const agencyId = computed<number>(() => applicationStore.agencyIdSelected || 0);
const includeConnected = ref(false);
const fromDateTime = ref(undefined as string | undefined);
const toDateTime = ref(undefined as string | undefined);
const selectedProformaId = computed<number>(() => {
  if (selectedProformaIds.value.length === 0) {
    return 0;
  }
  return selectedProformaIds.value[0];
});
const emailTemplateGuid = ref<string | undefined>(undefined);

const hasPortcallData = computed(() => agencyProformas.value.some((row) => row.PORTCALL_NUMBER));

const columns = computed(() => {
  const result: ColumnDef<EnquiryListItem>[] = [
    // { header: 'Enquiry no', field: 'IQY_ID', nowrap: true  },
    { header: 'Proforma no', field: 'PROFORMA_NUMBER', nowrap: true },
    { header: 'Created date', field: 'CREATED_DATE', nowrap: true }, // enquiry created date
    { header: 'Status', field: 'INQUERY_STATUS', nowrap: true },
    ...(hasPortcallData.value ? [{ header: 'Portcall', field: 'PORTCALL_NUMBER' as const, nowrap: true }] : []),
    { header: 'Vessel', field: 'VESSEL_NAME', nowrap: true },
    { header: 'Harbour', field: 'HNAME', nowrap: true },
    { header: 'DWT', field: 'DWT', dataType: 'number', nowrap: true },
    // { header: 'GT', field: 'GT', dataType: 'number', nowrap: true  },
    { header: 'Total', field: 'TOTAL', dataType: 'number', nowrap: true },
    // { header: 'Description', field: 'DESCRIPTION', nowrap: true  },
    { header: 'Client', field: 'CLIENT_NAME', nowrap: true },
    // { header: 'Client city', field: 'CLIENT_CITY', nowrap: true  },
    { header: 'Client country', field: 'CLIENT_COUNTRY', nowrap: true },
  ];
  return result;
});

const userCanGenerateDoc = computed<boolean>(() => {
  const isInternal = applicationStore.user.internal;
  if (isInternal) {
    return applicationStore.userRight('DOC.NEW_AND_EDIT');
  }
  return false;
});

const wmButtonGroup = computed(() => {
  const result = [];
  result.push({
    btnType: 'CREATE_DOC',
    docType: 2,
    agencyId: agencyId.value,
    location: 'proforma',
    foreignKey: selectedProformaId.value,
    disabled: !userCanGenerateDoc.value || selectedProformaId.value === 0,
  });
  result.push({
    btnType: 'CUSTOM',
    caption: 'Create email',
    icon: 'mdi-email',
    menuItems: emailTemplates.value,
    disabled: emailTemplates.value.length === 0 || selectedProformaId.value === 0,
  });
  return result;
});

function emailGeneratorIsLoading(event: boolean) {
  if (!event) {
    emailTemplateGuid.value = undefined;
  }
}

function createEmailFromTemplateClick(item: ProformaEmailTemplateItem) {
  // create new email with template
  if (selectedProformaId.value && item.ID) {
    const guid = GlobalHelperFunctions.GUID();
    generateDocument('proforma', selectedProformaId.value, item.ID, guid, 1).then((result) => {
      if (result.resultCategory === 0 && result.resultType === 0) {
        emailTemplateGuid.value = guid;
        /* this.showEmailStatusDialog = true; */
      }
    });
  }
}

async function getProformas() {
  isLoading.value = true;
  try {
    if (fromDateTime.value && toDateTime.value) {
      await proformaStore.fetchAgencyProformas(fromDateTime.value, toDateTime.value, includeConnected.value);
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
}

async function setDefaultFilterValues() {
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 12); // Default to 12 months ago
  const endDate = new Date();
  fromDateTime.value = GlobalHelperFunctions.globalFormatDate(startDate, 'YYYY-MM-DD');
  toDateTime.value = GlobalHelperFunctions.globalFormatDate(endDate, 'YYYY-MM-DD');
}

function proformaSelected(proformaIDs: number[]) {
  selectedProformaIds.value = [];
  if (proformaIDs.length > 1) {
    selectedProformaIds.value.push(proformaIDs[proformaIDs.length - 1]);
  }
  if (proformaIDs.length === 1) {
    selectedProformaIds.value.push(proformaIDs[0]);
  }
}

function enquiryClicked(rowItem: EnquiryListItem) {
  const params = {
    callId: rowItem.PORTCALL_ID?.toString() ?? '',
    proformaId: `${rowItem.PROFORMA_ID}`,
  };
  router.push({ name: 'proformaDetails', params });
}

onActivated(() => {
  setDefaultFilterValues().then(() => {
    getProformas();
  });
});

watch(
  () => agencyId.value,
  async (newValue) => {
    if (!toDateTime.value || !fromDateTime.value) {
      await setDefaultFilterValues();
    }
    if (newValue) {
      await getProformas();
    }
  },
  { immediate: true }
);

return { __sfc: true,proformaStore, applicationStore, isLoading, agencyProformas, emailTemplates, selectedProformaIds, agencyId, includeConnected, fromDateTime, toDateTime, selectedProformaId, emailTemplateGuid, hasPortcallData, columns, userCanGenerateDoc, wmButtonGroup, emailGeneratorIsLoading, createEmailFromTemplateClick, getProformas, setDefaultFilterValues, proformaSelected, enquiryClicked, EmailGenerator }
}

})